// src/components/ContactForm.js - Wersja z przekierowaniem i GTM

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom'; // KROK 1: Import hooka do nawigacji
import TagManager from 'react-gtm-module';     // KROK 1: Import GTM
import { ArrowRight } from 'lucide-react';

// Ten fragment najlepiej przenieść do public/index.html lub głównego komponentu App, aby nie tworzyć go przy każdym renderowaniu formularza
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);

const ContactForm = () => {
  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const navigate = useNavigate(); // KROK 2: Inicjalizacja hooka nawigacji

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      nip: formData.get('nip'),
      topic: formData.get('topic'),
      message: formData.get('message') || '',
      timestamp: new Date().toISOString(),
      source: 'strona-szynkowski'
    };
    
    try {
      const response = await fetch('https://n8n.agencjasmart.marketing/webhook/szynkowski-formularz', {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(data)
      });
      
      // Sprawdzamy, czy wysyłka się udała
      if (response.ok || response.status === 200 || response.type === 'opaque') {
        
        // KROK 3: Wysyłamy zdarzenie do Google Tag Managera
        TagManager.dataLayer({
          dataLayer: {
            event: 'form_submission_success',
            formName: 'Główny formularz kontaktowy',
            formTopic: data.topic // Dodajemy temat, żeby wiedzieć czego dotyczy lead
          }
        });

        // KROK 4: Przekierowanie na stronę z podziękowaniami!
        navigate('/dziekujemy');

      } else {
        // Jeśli jest błąd, rzucamy go, aby obsłużyć w bloku catch
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        // W przypadku błędu CORS, również przekierowujemy - prawdopodobnie wysyłka się udała
        TagManager.dataLayer({
          dataLayer: {
            event: 'form_submission_success', // Rejestrujemy jako sukces
            formName: 'Główny formularz kontaktowy',
            formTopic: data.topic,
            submissionType: 'cors-fallback' // Oznaczamy, że to fallback
          }
        });
        navigate('/dziekujemy'); // Przekieruj nawet przy błędzie CORS
      } else {
        setSubmitStatus('error');
        if (formRef.current) {
          formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setTimeout(() => setSubmitStatus(null), 10000);
      }
    } finally {
      // isSubmitting jest ustawiane na false tylko w przypadku błędu. 
      // Przy sukcesie komponent jest odmontowywany, więc nie ma potrzeby tego robić.
      if (!isSubmitting) {
          setIsSubmitting(false);
      }
    }
  };

  return (
    <section id="kontakt" ref={formRef} className="py-6 md:py-8 relative overflow-hidden bg-black">
      <div className="absolute inset-0"></div>
      
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="backdrop-blur-md bg-white/5 rounded-2xl md:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/10 shadow-2xl shadow-white/5">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-6 text-center uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
            ZRÓBMY RAZEM COŚ WIELKIEGO
          </h2>
          
          {/* Wyświetlanie błędu pozostaje bez zmian */}
          {submitStatus === 'error' && (
            <div className="mb-4 rounded-lg text-center text-sm transition-opacity duration-300">
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-3 rounded-lg">
                <p className="font-semibold">Ups! Coś poszło nie tak.</p>
                <p>Spróbuj ponownie lub zadzwoń: 791 335 232</p>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-3">
             {/* ... reszta formularza bez zmian ... */}
             <div>
               <label className="block text-xs font-medium text-gray-400 mb-1">Imię i nazwisko <span className="text-red-400">*</span></label>
               <input type="text" name="name" required disabled={isSubmitting} className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-colors disabled:opacity-50 text-sm" placeholder="Jan Kowalski" />
             </div>
             
             <div>
               <label className="block text-xs font-medium text-gray-400 mb-1">Email <span className="text-red-400">*</span></label>
               <input type="email" name="email" required disabled={isSubmitting} className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-colors disabled:opacity-50 text-sm" placeholder="jan@firma.pl" />
             </div>
             
             <div>
               <label className="block text-xs font-medium text-gray-400 mb-1">Telefon <span className="text-red-400">*</span></label>
               <input type="tel" name="phone" required disabled={isSubmitting} className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-colors disabled:opacity-50 text-sm" placeholder="+48 123 456 789" />
             </div>

             <div>
               <label className="block text-xs font-medium text-gray-400 mb-1">NIP firmy <span className="text-red-400">*</span></label>
               <input type="text" name="nip" required pattern="[0-9]{10}" title="NIP musi składać się z 10 cyfr" disabled={isSubmitting} className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-colors disabled:opacity-50 text-sm" placeholder="1234567890" />
             </div>
             
             <div className="lg:col-span-2">
               <label className="block text-xs font-medium text-gray-400 mb-1">W czym możemy pomóc? <span className="text-red-400">*</span></label>
               <select name="topic" required disabled={isSubmitting} className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-violet-500 transition-colors disabled:opacity-50 text-sm">
                 <option value="" className="bg-gray-900">Wybierz temat</option>
                 <option value="dotacje" className="bg-gray-900">Dotacje unijne</option>
                 <option value="prawo" className="bg-gray-900">Obsługa prawna</option>
                 <option value="podatki" className="bg-gray-900">Optymalizacja podatkowa</option>
                 <option value="inne" className="bg-gray-900">Inne</option>
               </select>
             </div>
             
             <div className="lg:col-span-2">
               <label className="block text-xs font-medium text-gray-400 mb-1">Wiadomość <span className="text-gray-500 text-xs">(opcjonalnie)</span></label>
               <textarea name="message" rows="2" disabled={isSubmitting} className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-colors resize-none disabled:opacity-50 text-sm" placeholder="Możesz opisać szczegóły swojego projektu lub zadać dodatkowe pytania..."></textarea>
             </div>
             
             <div className="lg:col-span-2">
               <label className="flex items-start gap-3 cursor-pointer">
                 <input type="checkbox" name="consent" required disabled={isSubmitting} className="mt-0.5 w-4 h-4 rounded border-gray-600 bg-white/5 text-violet-700 focus:ring-violet-600 disabled:opacity-50" />
                 <span className="text-xs text-gray-400">
                   Wyrażam zgodę na przetwarzanie moich danych osobowych w celu obsługi zapytania oraz przesyłania informacji handlowych. 
                   <a href="/polityka-prywatnosci" className="text-violet-500 hover:text-violet-400"> Polityka prywatności</a>
                 </span>
               </label>
             </div>
             
             <div className="lg:col-span-2 text-center mt-3">
               <button 
                 type="submit"
                 disabled={isSubmitting}
                 className="w-full lg:w-auto bg-gradient-to-r from-violet-600 to-violet-700 text-white px-6 sm:px-12 py-3 rounded-full font-bold text-base sm:text-lg hover:shadow-2xl hover:shadow-violet-600/20 hover:scale-105 transition-all duration-300 group disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none"
               >
                 <span className="flex items-center justify-center gap-3">
                   {isSubmitting ? (
                     <>
                       <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                       Wysyłanie...
                     </>
                   ) : (
                     <>
                       Wyślij zapytanie
                       <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                     </>
                   )}
                 </span>
               </button>
             </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;